/**
 * Feed sort helpers.
 *
 * Every "live-first" feed (My Prayers, Circle page, Shared tab) shows
 * prayers currently being prayed on top, followed by everyone else
 * sorted by whatever secondary rule that surface uses. The live count
 * per prayer comes from `POST /api/prayers/live-counts`, polled every
 * ~5s by the calling page — this helper takes that map and re-sorts
 * the visible list in place.
 *
 * The sort is stable (single-pass comparator returning 0 when both
 * are non-live), so prayers that all have zero live count keep the
 * order the server returned them in.
 */

/**
 * Return a NEW prayers array sorted so `live_count > 0` prayers come
 * first, then the rest, each partition sorted by `getSecondaryTime(p)`
 * descending (newer = earlier in the list).
 *
 * @param prayers  Array of prayer docs (won't be mutated).
 * @param liveCountsById  Map { [prayer_id]: liveCount } from the poll.
 * @param getSecondaryTime  Function returning a number for `sort` — a
 *   ms epoch works well. Defaults to `updated_at || created_at`.
 */
export function sortLiveFirst(prayers, liveCountsById, getSecondaryTime) {
  if (!Array.isArray(prayers) || prayers.length === 0) return prayers || [];
  const getTime = typeof getSecondaryTime === 'function'
    ? getSecondaryTime
    : (p) => Date.parse(p?.updated_at || p?.created_at || 0) || 0;
  const liveFor = (p) => {
    // `liveCountsById` is authoritative once the first poll comes back;
    // fall back to the doc's server-side snapshot value only until
    // then so the first render isn't visually inert.
    const fromPoll = liveCountsById && liveCountsById[p.id];
    if (typeof fromPoll === 'number') return fromPoll;
    return Number(p?.online_praying_now_count) || 0;
  };
  return [...prayers].sort((a, b) => {
    const aLive = liveFor(a) > 0 ? 1 : 0;
    const bLive = liveFor(b) > 0 ? 1 : 0;
    if (aLive !== bLive) return bLive - aLive; // live first
    return getTime(b) - getTime(a); // then secondary desc
  });
}

/**
 * Secondary time-getter for "My Prayers" / "Circle" — last edited time,
 * falling back to created time for prayers never edited.
 */
export const editedTime = (p) => (
  Date.parse(p?.updated_at || p?.created_at || 0) || 0
);

/**
 * Secondary time-getter for "Shared" — when THIS viewer opened the
 * shared link (the ledger row's `opened_at`). Falls back to the
 * prayer's own edited/created time for defensiveness.
 */
export const openedTime = (p) => (
  Date.parse(p?.opened_at || p?.updated_at || p?.created_at || 0) || 0
);
